/** The pieces a clause is shown with wherever it appears: its status symbol, its labels,
 * its row and its details (the groups it is read in are ClauseGroups.tsx).
 * Shared by a document's clause page and each specialist's "Documented obligations" card. */
import { type HTMLAttributes, type ReactNode, useState } from "react";
import { Link } from "react-router-dom";

import type { GroupStatus, ItemState, LineRef, RegisterGoal, RegisterLine } from "../api";
import { formatDay } from "../utils/attributeFormat";
import styles from "./Clauses.module.css";
import {
  type Counts,
  countedKinds,
  shapeLabels,
  KIND_LABEL,
  KIND_OF,
  KIND_SYMBOL,
  kindCounts,
  SUMMARY_KINDS,
  STATUS_LABEL,
  type StatusKind,
  symbolTone,
  ITEM_LABEL,
  ITEM_SYMBOL,
  type ItemKind,
  itemTone,
  shownReference,
} from "./clauseStatus";
import { Chevron } from "./Chevron";

const Glyph = ({ kind }: { kind: StatusKind }) => (
  <span className={`${styles.symbol} ${symbolTone(kind)}`} aria-hidden="true">
    {KIND_SYMBOL[kind]}
  </span>
);

/** A status as its symbol, read out in words. When a not-due clause comes back sits at
 * the row's end (``dueText``), so the symbols stay one aligned column. */
export const StatusSymbol = ({ status }: { status: GroupStatus }) => (
  <span className={styles.statusCell} title={STATUS_LABEL[status]}>
    <Glyph kind={KIND_OF[status]} />
    <span className={styles.srOnly}>{STATUS_LABEL[status]}</span>
  </span>
);

/** A goal's or section's clauses by status, as symbols with their counts. */
export const SymbolCounts = ({ counts }: { counts: Counts }) => {
  const kinds = kindCounts(counts);
  return (
    <span className={styles.symbolCounts}>
      {SUMMARY_KINDS.filter((k) => kinds[k]).map((k) => (
        <span key={k} title={KIND_LABEL[k]}>
          <Glyph kind={k} />
          <span className={styles.srOnly}>{KIND_LABEL[k]}:</span> {kinds[k]}
        </span>
      ))}
    </span>
  );
};

/** A summary of statuses in words, each led by its symbol: "✕ 1 deviate · ✓ 8 met". */
export const CountsLine = ({ counts, empty }: { counts: Counts; empty: string }) => {
  const parts = countedKinds(counts);
  if (parts.length === 0) return <span className={styles.muted}>{empty}</span>;
  return (
    <span className={styles.countsLine}>
      {parts.map(([kind, text], i) => (
        <span key={kind}>
          {i > 0 && " · "}
          <Glyph kind={kind} /> {text}
        </span>
      ))}
    </span>
  );
};

export const StatusLegend = () => (
  <ul className={styles.legend} aria-label="What each symbol means">
    {SUMMARY_KINDS.map((k) => (
      <li key={k}>
        <Glyph kind={k} /> {KIND_LABEL[k]}
      </li>
    ))}
  </ul>
);

/** What a clause is beyond its title: whose it is, what kind, its condition, the choice
 * it is one option of, what it needs, what disputes it, and its topics. */
export const ClauseLabels = ({
  line,
  options,
  agentLabel,
  showSection,
  viewer,
}: {
  line: RegisterLine;
  /** How many options its choice has. */
  options: number;
  agentLabel: (agent: string) => string;
  /** Where the rows are not grouped by section, the section each sits in. */
  showSection: boolean;
  /** The specialist whose card it is on, if any. */
  viewer: string | null;
}) => {
  const section = line.section_path[line.section_path.length - 1];
  const plain = styles.badge;
  const warn = `${styles.badge} ${styles.badgeWarn}`;
  const labels: [string, string][] = [
    ...shapeLabels(line).map((l): [string, string] => [l, plain]),
    ...(line.applies_when ? [["Conditional", plain] as [string, string]] : []),
    ...(line.one_of && options > 1 ? [[`One of ${options} options`, plain] as [string, string]] : []),
    ...(line.entry === "list"
      ? [[`List of ${line.parts.length} items`, plain] as [string, string]]
      : []),
    // When it falls due, what it may need, its form, limits and topics are in its details:
    // the collapsed row keeps only what changes how it is read or blocks it.
    ...(line.reclassified ?? []).map((r): [string, string] => [
      r.agent === viewer ? "Taken on as ours" : `Reclassified by ${agentLabel(r.agent)}`,
      warn,
    ]),
    // Only a clause waiting for a clause list is held up; any other need is advice.
    ...(line.depends_on && line.standing === "awaiting_document"
      ? [[`Waiting for ${line.depends_on.document}`, warn] as [string, string]]
      : []),
    ...line.conflicts.map((c): [string, string] => [`Conflicts with ${c.reference}`, warn]),
    ...(showSection && section ? [[`in ${section}`, styles.sectionRef] as [string, string]] : []),
  ];
  // Spaces between the labels, so the row copies and reads as separate words.
  return (
    <>
      {labels.map(([text, className]) => (
        <span key={text}>
          {" "}
          <span className={className}>{text}</span>
        </span>
      ))}
    </>
  );
};

const RefLink = ({ to, refHref }: { to: LineRef; refHref: (lineId: string) => string }) => (
  <Link to={refHref(to.line_id)}>
    {to.reference}
    {to.title ? ` (${to.title})` : ""}
  </Link>
);

const Refs = ({ refs, refHref }: { refs: LineRef[]; refHref: (lineId: string) => string }) => (
  <>
    {refs.map((r, i) => (
      <span key={r.line_id}>
        {i > 0 && ", "}
        <RefLink to={r} refHref={refHref} />
      </span>
    ))}
  </>
);

/** A clause's details: first what needs to be satisfied (its condition, its words, the
 * other options of its choice, the definitions it uses, its links), then, on a project,
 * why it is or is not. */
export const ClauseDetails = ({
  line,
  otherOptions,
  definitionTexts,
  refHref,
  agentLabel,
  why,
  topicLabel,
}: {
  line: RegisterLine;
  agentLabel: (agent: string) => string;
  /** Its topics' names, listed in the details rather than on the row. */
  topicLabel?: (key: string) => string;
  /** The other options of its choice. */
  otherOptions: LineRef[];
  definitionTexts: Record<string, string>;
  /** Where a link to another clause of the same document goes. */
  refHref: (lineId: string) => string;
  /** The verdict on a project; null off one. */
  why: ReactNode;
}) => {
  return (
  <div className={styles.details}>
    {/* The document's words lead: agents assess against them. A statement is written
        only where the words cannot be read alone, so it shows beside them. */}
    {(line.classification === "requirement" || line.contested) && (
      <h4 className={styles.detailsHead}>
        What the document says
        {line.file ? ` (${line.file.name}, page ${line.file.page})` : ""}
      </h4>
    )}
    {line.applies_when && <p className={styles.applies}>Applies when: {line.applies_when}</p>}
    <p className={styles.text}>{line.text}</p>
    {line.statement && (
      <p className={styles.statement}>
        <span className={styles.statementLabel}>Read in context:</span> {line.statement}
      </p>
    )}
    {otherOptions.length > 0 && (
      <p className={styles.assessment}>
        <span className={styles.muted}>Or instead, one of: </span>
        <Refs refs={otherOptions} refHref={refHref} />
      </p>
    )}
    {line.definitions.length > 0 && (
      <dl className={styles.definitions}>
        {line.definitions.map((d) => (
          <div key={d.line_id}>
            <dt>{d.term}</dt>
            <dd>{definitionTexts[d.line_id] ?? d.title}</dd>
          </div>
        ))}
      </dl>
    )}
    {(line.states_due_by || line.due_rule) && (
      <p className={styles.assessment}>
        <span className={styles.muted}>When the document says it falls due: </span>
        {line.states_due_by ? formatDay(line.states_due_by) : line.due_rule}
      </p>
    )}
    {line.depends_on && (line.classification === "requirement" || line.contested) && (
      <p className={styles.assessment}>
        <span className={styles.muted}>
          {line.depends_on.file_id
            ? "Judged with a Global data file of this document's folder: "
            : line.standing === "awaiting_document"
              ? "Waiting for a document no clause list here holds: "
              : "Not in this clause list, but needed to judge it fully: "}
        </span>
        {line.depends_on.document}
        {!line.depends_on.file_id && line.standing !== "awaiting_document" && (
          <span className={styles.muted}>
            {" "}
            (assessed on what is held, saying what is missing)
          </span>
        )}
      </p>
    )}
    {topicLabel && line.topics.length > 0 && (
      <p className={styles.assessment}>
        <span className={styles.muted}>Topics: </span>
        {line.topics.map(topicLabel).join(", ")}
      </p>
    )}
    {line.form && (
      <p className={styles.assessment}>
        <span className={styles.muted}>Inside, not yet executed: </span>
        {line.form}
      </p>
    )}
    {line.restates && (
      <p className={styles.assessment}>
        <span className={styles.muted}>Restates </span>
        <RefLink to={line.restates} refHref={refHref} />, whose status it shows.
      </p>
    )}
    {line.restated_by.length > 0 && (
      <p className={styles.assessment}>
        <span className={styles.muted}>Restated by </span>
        <Refs refs={line.restated_by} refHref={refHref} />
      </p>
    )}
    {line.conflicts.map((c) => (
      <p key={c.line_id} className={styles.contested}>
        Conflicts with <RefLink to={c} refHref={refHref} />
        {c.note ? `: ${c.note}` : ""}
      </p>
    ))}
    {line.limits.map((l) => (
      <p key={l.line_id} className={styles.assessment}>
        <span className={styles.muted}>
          {l.stricter ? "Stricter than " : "A stricter limit is in "}
        </span>
        <RefLink to={l} refHref={refHref} />
        {l.note ? `: ${l.note}` : ""}
      </p>
    ))}
    {line.entry !== "list" && line.parts.length > 0 && (
      <div className={styles.assessment}>
        <span className={styles.muted}>Checked for:</span>
        <ul className={styles.criteria}>
          {line.parts.map((p) => (
            <li key={p.line_id}>{p.title || p.reference}</li>
          ))}
        </ul>
      </div>
    )}
    {line.group_condition && (
      <p className={styles.applies}>Its section applies only if: {line.group_condition}</p>
    )}
    {line.routing_note && (
      <p className={styles.muted}>Routing note: {line.routing_note}</p>
    )}
    {(line.reclassified ?? [])
      // A reason that is word for word the verdict's reasoning is said once, there.
      .filter((r) => r.reason !== line.by_agent?.find((a) => a.agent === r.agent)?.assessment?.reason)
      .map((r) => (
      <p key={r.agent} className={styles.contested}>
        Reclassified by {agentLabel(r.agent)}: {r.reason}
      </p>
    ))}
    {why && (
      <>
        <h4 className={styles.detailsHead}>Why it is or isn&apos;t satisfied</h4>
        {why}
      </>
    )}
  </div>
  );
};

/** One clause as a checklist row: the whole row opens its details. */
export const ClauseRow = ({
  line,
  marker,
  title,
  labels,
  trailing,
  details,
  rowProps,
  below,
}: {
  line: RegisterLine;
  /** Shown under the row whether or not it is open: a list's items. */
  below?: ReactNode;
  /** The status symbol, or a neutral marker off a project. */
  marker: ReactNode;
  /** The clause's title, or what a non-requirement is. */
  title: string;
  labels: ReactNode;
  trailing: ReactNode;
  details: () => ReactNode;
  rowProps?: HTMLAttributes<HTMLLIElement>;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <li id={line.line_id} className={styles.line} {...rowProps}>
      <button
        type="button"
        className={styles.rowButton}
        aria-expanded={open}
        onClick={(e) => {
          // Selecting the row's words to comment on them is not a click on the row.
          const selection = window.getSelection();
          if (
            selection &&
            !selection.isCollapsed &&
            e.currentTarget.contains(selection.anchorNode)
          )
            return;
          setOpen((v) => !v);
        }}
      >
        <span className={styles.marker}>{marker}</span>{" "}
        <span className={styles.rowMain}>
          <span className={styles.reference}>{shownReference(line)}</span>{" "}
          <span className={styles.lineTitle}>{title}</span>
          {labels}
        </span>{" "}
        {trailing && <span className={styles.rowTrailing}>{trailing}</span>}
        <span className={styles.chevron}>
          <Chevron tree open={open} />
        </span>
      </button>
      {open && details()}
      {below}
    </li>
  );
};

const ItemGlyph = ({ kind }: { kind: ItemKind }) => (
  <span className={styles.statusCell} title={ITEM_LABEL[kind]}>
    <span className={`${styles.symbol} ${itemTone(kind)}`} aria-hidden="true">
      {ITEM_SYMBOL[kind]}
    </span>
    <span className={styles.srOnly}>{ITEM_LABEL[kind]}</span>
  </span>
);

/** A list obligation's items, closed at first: each with its state on a project (or a
 * neutral marker off one) and its note, under the sub-headings the document gives them,
 * with the numbers its table skips explained. */
export const ListItems = ({
  entry,
  goal,
  lineOf,
  itemOf,
  details,
}: {
  entry: RegisterLine;
  /** The package or list goal its members make up, for the gaps in its numbering. */
  goal: RegisterGoal | null;
  /** A member's whole clause, where the page holds it. */
  lineOf: (lineId: string) => RegisterLine | undefined;
  /** A member's state on the project; undefined off one. */
  itemOf: ((lineId: string) => { state: ItemState; note: string } | null) | undefined;
  details: (line: RegisterLine) => ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  const members = entry.parts;
  if (members.length === 0) return null;
  // Under the sub-headings the document gives them, where it gives more than one.
  const headingOf = (id: string) => {
    const path = lineOf(id)?.section_path ?? [];
    return path[path.length - 1] ?? "";
  };
  const headings = [...new Set(members.map((m) => headingOf(m.line_id)))];
  const byHeading = headings.length > 1;
  const row = (part: LineRef) => {
    const item = itemOf?.(part.line_id);
    const marker = itemOf ? (
      <ItemGlyph kind={item?.state ?? "unstated"} />
    ) : (
      <span className={styles.neutralMarker} aria-hidden="true">
        ▢
      </span>
    );
    const full = lineOf(part.line_id);
    const note = item?.note ? <span className={styles.muted}>{item.note}</span> : null;
    return full ? (
      <ClauseRow
        key={part.line_id}
        line={full}
        marker={marker}
        title={full.document_title ?? full.title}
        labels={null}
        trailing={note}
        details={() => details(full)}
      />
    ) : (
      <li key={part.line_id} className={styles.line}>
        <span className={styles.rowButton}>
          <span className={styles.marker}>{marker}</span>{" "}
          <span className={styles.rowMain}>
            <span className={styles.reference}>{part.reference}</span>{" "}
            <span className={styles.lineTitle}>{part.title}</span>
          </span>{" "}
          {note && <span className={styles.rowTrailing}>{note}</span>}
        </span>
      </li>
    );
  };
  return (
    <div className={styles.listItems}>
      <button
        type="button"
        className={styles.toggle}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <Chevron tree open={open} /> {open ? "Hide" : "Show"} its {members.length} items
      </button>
      {open && (
        <>
          {goal && goal.skipped_numbers.length > 0 && (
            <p className={styles.muted}>
              Numbers {goal.skipped_numbers.join(", ")} are not used in the source.
            </p>
          )}
          {goal?.numbers_elsewhere.map((n) => (
            <p key={n.line_id} className={styles.muted}>
              Number {n.number} is stated at {n.reference}.
            </p>
          ))}
          {byHeading ? (
            headings.map((h) => (
              <div key={h}>
                {h && <h5 className={styles.itemHeading}>{h}</h5>}
                <ul className={styles.list}>
                  {members.filter((m) => headingOf(m.line_id) === h).map(row)}
                </ul>
              </div>
            ))
          ) : (
            <ul className={styles.list}>{members.map(row)}</ul>
          )}
        </>
      )}
    </div>
  );
};
